import type { PersonaTemper, SiteKind } from './site-kind.ts';
import type { SiteReviewDto, SiteReviewStatus } from './analysis.ts';
import { z } from 'zod';
import type { Device } from './events.ts';
import { SITE_LANGUAGES, type SiteLanguage } from './markets.ts';
import { personaTraitsSchema, VISIT_STAGES, type SampledTraits, type VisitStage } from './persona-traits.ts';
import { TEST_ACCESS_MODES, type TestAccessDto, type VisitAccessDto } from './access.ts';

/** Synthetic users: AI agents with personas that visit a site and get recorded like real visitors. */

export const REPORT_LANGUAGES = ['en', 'it'] as const;
export type ReportLanguage = (typeof REPORT_LANGUAGES)[number];

export const VISIT_MODES = ['natural', 'mission'] as const;
export type VisitMode = (typeof VISIT_MODES)[number];

/**
 * What the test finds out. `human` ("where I lose people"): ordinary people with a reason to be there, held
 * to human limits (what they notice, their time, their patience) and leaving for real reasons. `check`
 * ("what is wrong with the site", shown as Inspection): the personas' devices and needs, but an inspector's
 * eyes, no clock and no giving up, reporting every problem screen by screen. The free analysis is always human.
 */
export const TEST_BEHAVIOURS = ['human', 'check'] as const;
export type TestBehaviour = (typeof TEST_BEHAVIOURS)[number];

/**
 * `converted`: did the action. `satisfied`: got what they came for without converting (an inspection that
 * covered the site ends here too). `abandoned`: left without what they wanted, the signal about the site.
 * `interrupted`: stopped by our limits (steps, time on our side), left out of the rates. `failed` and
 * `blocked`: our error or an anti-bot wall, refunded. `login_wall`: stopped at a sign-in page without an
 * account to use: not a choice of the visitor, so left out of the rates and kept apart from those who left.
 */
export const VISIT_STATUSES = ['queued', 'running', 'converted', 'satisfied', 'abandoned', 'interrupted', 'failed', 'blocked', 'login_wall', 'cancelled'] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];
/** Outcomes a runner can report when a visit ends. */
export const VISIT_RESULTS = ['converted', 'satisfied', 'abandoned', 'interrupted', 'failed', 'blocked', 'login_wall'] as const;
export type VisitResult = (typeof VISIT_RESULTS)[number];
/** Visits that count in the rates: the person decided how it ended. */
export const COUNTED_STATUSES: readonly VisitStatus[] = ['converted', 'satisfied', 'abandoned'];

/** Why a visitor left, as they say it (and as the engine says it when it ends the visit). */
export const EXIT_REASONS = [
  'got_what_i_needed',
  'not_found',
  'price',
  'confusing',
  'trust',
  'site_error',
  'out_of_time',
  'too_frustrated',
  'just_browsing',
  'other_site',
] as const;
export type ExitReason = (typeof EXIT_REASONS)[number];

export const exitReasonLabel: Record<ExitReason, string> = {
  got_what_i_needed: 'Got what they needed',
  not_found: 'Could not find what they came for',
  price: 'Price or offer did not fit',
  confusing: 'Did not understand',
  trust: 'Did not trust the site',
  site_error: 'Something on the site broke',
  out_of_time: 'Ran out of time',
  too_frustrated: 'Too frustrated to go on',
  just_browsing: 'Was just looking',
  other_site: 'Went to another site',
};

export const GOAL_REACHED = ['yes', 'partly', 'no'] as const;
export type GoalReached = (typeof GOAL_REACHED)[number];

/** How the visitor feels on a screen; the history of feelings carries irritation from page to page. */
export const FEELINGS = ['neutral', 'interested', 'reassured', 'confused', 'annoyed', 'bored', 'stuck'] as const;
export type Feeling = (typeof FEELINGS)[number];
export const POSITIVE_FEELINGS: readonly Feeling[] = ['interested', 'reassured'];
export const NEGATIVE_FEELINGS: readonly Feeling[] = ['confused', 'annoyed', 'stuck', 'bored'];

export const TEST_STATUSES = ['running', 'reporting', 'completed', 'cancelled'] as const;
export type TestStatus = (typeof TEST_STATUSES)[number];

export const ISSUE_SEVERITIES = ['low', 'medium', 'high'] as const;
export type IssueSeverity = (typeof ISSUE_SEVERITIES)[number];

/**
 * Hard caps of one visit, the same for every plan. They are safety limits, not behaviour: the persona's
 * time and patience end a visit long before. Reaching one ends the visit as `interrupted`, never `failed`.
 * The agent is never told the cap.
 */
export const VISIT_MAX_STEPS = 50;
/** Step cap by test: natural visits, missions and inspections (which must cover the site). */
export function stepCap(mode: VisitMode, behaviour: TestBehaviour): number {
  if (behaviour === 'check') return VISIT_MAX_STEPS;
  return mode === 'mission' ? 35 : 25;
}
export const VISIT_MAX_DURATION_MS = 15 * 60 * 1000;
/** Max visits of one test in the PoC. */
export const TEST_MAX_VISITS = 50;

const deviceEnum = z.enum(['desktop', 'tablet', 'mobile']);
const shortText = (max: number) => z.string().trim().min(1).max(max);
const pathString = z
  .string()
  .trim()
  .max(512)
  .refine((v) => v.startsWith('/'), { message: 'Use a path starting with /' });

export const personaCoreSchema = z.object({
  name: shortText(80),
  summary: shortText(1000),
  age: z.number().int().min(12).max(100).nullable().default(null),
  occupation: z.string().trim().max(120).nullable().default(null),
  device: deviceEnum.default('desktop'),
  /** 1 = struggles with technology, 5 = power user. */
  techSavvy: z.number().int().min(1).max(5).default(3),
  /** 1 = leaves at the first obstacle, 5 = very persistent. */
  patience: z.number().int().min(1).max(5).default(3),
  goals: z.array(shortText(200)).max(8).default([]),
  frustrations: z.array(shortText(200)).max(8).default([]),
  /** Language of thoughts and typed text; null = the site language (`<html lang>`). */
  language: z
    .string()
    .trim()
    .regex(/^[a-z]{2}(-[A-Za-z]{2})?$/)
    .nullable()
    .default(null),
  /** Plausible reasons to visit; each visit picks one (natural mode). */
  intents: z.array(shortText(300)).min(1).max(8),
  /** Paths where this persona may land. */
  entryPaths: z.array(pathString).min(1).max(5).default(['/']),
  /** The configurator's traits; null for personas made before it (derived from techSavvy and patience). */
  traits: personaTraitsSchema.nullable().default(null),
});

export type PersonaCore = z.infer<typeof personaCoreSchema>;
export type PersonaCoreInput = z.input<typeof personaCoreSchema>;

export const createPersonaSchema = personaCoreSchema.extend({
  presetKey: z.string().max(64).optional(),
});
export const updatePersonaSchema = personaCoreSchema.partial();

export const conversionGoalSchema = z.object({
  description: shortText(300),
  /** Path pattern with `*` wildcards; reaching it counts as a conversion. */
  urlPattern: pathString.optional(),
  /** Custom event name (`daevio('track', name)`) that counts as a conversion. */
  eventName: z.string().trim().min(1).max(256).optional(),
});
export type ConversionGoal = z.infer<typeof conversionGoalSchema>;

export const missionSchema = z.object({
  task: shortText(500),
  successCondition: shortText(500),
  /** Optional objective check: reaching this path pattern means success. */
  successUrlPattern: pathString.optional(),
});
export type Mission = z.infer<typeof missionSchema>;

const httpUrl = z
  .string()
  .trim()
  .max(2048)
  .refine(
    (v) => {
      try {
        const u = new URL(v);
        return u.protocol === 'http:' || u.protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'Use a full URL like https://www.example.com' },
  );

export const createSyntheticTestSchema = z
  .object({
    name: z.string().trim().max(120).optional(),
    siteUrl: httpUrl,
    personaIds: z.array(z.uuid()).min(1).max(20),
    visits: z.number().int().min(1).max(TEST_MAX_VISITS),
    mode: z.enum(VISIT_MODES).default('natural'),
    behaviour: z.enum(TEST_BEHAVIOURS).default('human'),
    /** Language of the site's content (shown in the form, detected from the pages); missing: detected before the visits. */
    siteLanguage: z.enum(SITE_LANGUAGES).optional(),
    /** Country the visitors browse from (ISO code); missing: from the domain and the language. */
    market: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{2}$/)
      .transform((v) => v.toUpperCase())
      .optional(),
    mission: missionSchema.optional(),
    conversion: conversionGoalSchema.optional(),
    /** Missing: the site's language when the report can be written in it, else English. */
    reportLanguage: z.enum(REPORT_LANGUAGES).optional(),
    /** Needed when the test targets a site other than the project's: the person confirms they may test it. */
    siteConsent: z.boolean().optional(),
    /** Sign-in pages: `auto` (default) uses the project's saved access or asks; `none` never signs in. */
    access: z.enum(TEST_ACCESS_MODES).optional(),
    seed: z
      .number()
      .int()
      .min(0)
      .max(2 ** 31 - 1)
      .optional(),
  })
  .refine((v) => v.mode !== 'mission' || v.mission !== undefined, { message: 'A mission needs a task and a success condition', path: ['mission'] });
export type CreateSyntheticTestInput = z.input<typeof createSyntheticTestSchema>;

/**
 * The concrete reason of one visit, written from the site's own pages before the visits start: a person
 * who searched for something specific, with a stage and a point where for them it is done.
 */
export interface VisitBrief {
  /** The need in the visitor's words, with one concrete detail ("waterproof boots in size 39, under €120"). */
  need: string;
  stage: VisitStage;
  /** The natural next step if it goes well: buy, sign up, contact, save it for later. */
  nextStep: string;
  /** When the visitor considers the visit done (an implicit success condition). */
  doneWhen: string;
}

export const visitBriefSchema = z.object({
  need: shortText(300),
  stage: z.enum(VISIT_STAGES).catch('comparing'),
  nextStep: z.string().trim().max(200).catch(''),
  doneWhen: z.string().trim().max(300).catch(''),
});

/** Variant of a persona for one visit: generated with seeded rules, no AI (the brief comes later, from the site). */
export interface VisitVariant {
  index: number;
  sourceLabel: string;
  referrer: string | null;
  utm: Record<string, string> | null;
  entryPath: string;
  /** The persona's reason for this visit; replaced by the brief's need once the brief is written. */
  intent: string;
  /** 1 = very frustrated, 10 = delighted. */
  initialMood: number;
  attention: 'low' | 'medium' | 'high';
  maxSteps: number;
  /** Seed of the visit's own random choices in the runner (mis-taps, giving up). Missing on older visits. */
  seed?: number;
  /** Stage of the visitor's decision for this visit. */
  stage?: VisitStage;
  /** Seconds the visitor has (simulated time); null: no clock (inspection). Missing on older visits. */
  timeBudgetS?: number | null;
  /** Values drawn from the persona's traits. Missing on older visits. */
  traits?: SampledTraits;
  /** Written from the site before the visits start; null when the pages could not be read. */
  brief?: VisitBrief | null;
}

const element = z.coerce.number().int().min(0);
/** Why the visitor leaves or gives up: category, what was missing in their words, whether they got what they came for. */
const exitFields = {
  category: z.enum(EXIT_REASONS).optional().catch(undefined),
  missing: z.string().max(300).optional().catch(undefined),
  goal: z.enum(GOAL_REACHED).optional().catch(undefined),
};

export const agentActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('click'), element }),
  z.object({ type: z.literal('type'), element, text: z.string().max(500), submit: z.boolean().optional() }),
  z.object({ type: z.literal('select'), element, option: z.string().max(200) }),
  z.object({ type: z.literal('scroll'), direction: z.enum(['up', 'down']).catch('down') }),
  z.object({ type: z.literal('back') }),
  z.object({ type: z.literal('wait') }),
  z.object({ type: z.literal('leave'), reason: z.string().max(500).catch(''), ...exitFields }),
  z.object({ type: z.literal('done'), success: z.boolean(), reason: z.string().max(500).catch(''), ...exitFields }),
]);
export type AgentAction = z.infer<typeof agentActionSchema>;

export const reportedIssueSchema = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).catch(''),
  severity: z.enum(ISSUE_SEVERITIES).catch('medium'),
});
export type ReportedIssue = z.infer<typeof reportedIssueSchema>;

const usageSchema = z.object({
  inputTokens: z.number().int().min(0),
  outputTokens: z.number().int().min(0),
  costUsd: z.number().min(0),
});

/** Body of `POST /internal/visits/:id/steps` (runner → server). */
export const visitStepSchema = z.object({
  index: z.number().int().min(0).max(1000),
  ts: z.number().int().positive(),
  url: z.string().max(4096),
  thought: z.string().max(2000),
  mood: z.number().int().min(1).max(10),
  feeling: z.enum(FEELINGS).nullable().default(null),
  action: agentActionSchema,
  result: z.string().max(1000).nullable(),
  /** Inspection: problems found on this screen. Ordinary visits leave it empty (the analyst finds them). */
  issues: z.array(reportedIssueSchema).max(10).default([]),
  /** Inspection: what was checked on this screen and found correct. */
  checked: z.array(z.string().trim().min(1).max(200)).max(10).default([]),
  /** Simulated time of the visitor when the step ended (ms since arrival). */
  clockMs: z.number().int().min(0).nullable().default(null),
  usage: usageSchema,
});
export type VisitStepInput = z.input<typeof visitStepSchema>;

export const visitExitSchema = z.object({
  category: z.enum(EXIT_REASONS),
  /** What was missing, in the visitor's words. */
  missing: z.string().max(300).nullable().default(null),
  goal: z.enum(GOAL_REACHED).nullable().default(null),
  /** The page where they left. */
  url: z.string().max(4096).nullable().default(null),
  /** Who ended the visit: the visitor, or the engine (time, frustration). */
  by: z.enum(['visitor', 'engine']).default('visitor'),
});
export type VisitExit = z.infer<typeof visitExitSchema>;

export const ISSUE_EFFECTS = ['caused_exit', 'slowed', 'annoyed'] as const;
export type IssueEffect = (typeof ISSUE_EFFECTS)[number];

export const analysisIssueSchema = z.object({
  stepIndex: z.number().int().min(0),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(1000).catch(''),
  /** What the visitor thought or did that shows it. */
  evidence: z.string().trim().max(500).catch(''),
  effect: z.enum(ISSUE_EFFECTS).catch('annoyed'),
});
export const analysisStrengthSchema = z.object({ stepIndex: z.number().int().min(0), title: z.string().trim().min(1).max(200), evidence: z.string().trim().max(500).catch('') });

/** What the end-of-visit analyst found in an ordinary visit: problems and strengths, each tied to a step. */
export const visitAnalysisSchema = z.object({
  issues: z.array(analysisIssueSchema).max(10).default([]),
  strengths: z.array(analysisStrengthSchema).max(3).default([]),
  /** The exit reason checked against the path, concrete, with the page. */
  exitReason: z.string().trim().max(500).nullable().default(null),
});
export type VisitAnalysis = z.infer<typeof visitAnalysisSchema>;
export type VisitStrength = VisitAnalysis['strengths'][number];

/** Severity of a problem the analyst found, from what it did to the visit. */
export const effectSeverity: Record<IssueEffect, IssueSeverity> = { caused_exit: 'high', slowed: 'medium', annoyed: 'low' };

/** Body of `POST /internal/visits/:id/finish` (runner → server). */
export const visitFinishSchema = z.object({
  status: z.enum(VISIT_RESULTS),
  outcome: z.string().max(500).nullable(),
  error: z.string().max(2000).nullable().default(null),
  durationMs: z.number().int().min(0),
  exit: visitExitSchema.nullable().default(null),
  analysis: visitAnalysisSchema.nullable().default(null),
  /** The visitor's simulated time on the site. */
  simulatedMs: z.number().int().min(0).nullable().default(null),
  /** Without a brief, the goal the visitor chose on arrival. */
  goal: z.string().trim().max(300).nullable().default(null),
  /** LLM usage of the end-of-visit analysis (the steps carry their own). */
  usage: usageSchema.nullable().default(null),
});
export type VisitFinishInput = z.input<typeof visitFinishSchema>;

/** True when `path` matches a pattern with `*` wildcards (e.g. `/checkout/*`). */
export function matchPathPattern(pattern: string, path: string): boolean {
  const re = new RegExp(`^${pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*')}$`);
  return re.test(path);
}

export interface PersonaDto extends PersonaCore {
  id: string;
  presetKey: string | null;
  /** One of the organization's own personas (written, drafted, or a ready-made one changed): counts in the plan. */
  own: boolean;
  /** Own, but beyond the plan's personas (after a move to a smaller plan): kept, not used in tests. */
  overLimit: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PresetPersonaDto extends PersonaCore {
  key: string;
  /** One of the personas every new project starts with; the others are added from the Personas page. */
  starter: boolean;
  /** Kinds of site this visitor makes sense on ("other": any site). */
  goodFor: SiteKind[];
  /** How hard they are on a site. */
  temper: PersonaTemper;
  /** How they think, in their words: the line the library shows. */
  quote: string;
}

export interface PersonaSnapshot extends PersonaCore {
  id: string;
}

/** What a runner needs to perform one visit (`GET /internal/visits/:id`). */
export interface VisitSpecDto {
  visitId: string;
  testId: string;
  sessionId: string;
  visitorId: string;
  publicKey: string;
  recorderUrl: string;
  siteUrl: string;
  entryUrl: string;
  persona: PersonaSnapshot;
  variant: VisitVariant;
  mode: VisitMode;
  /** Absent from servers older than the check mode: human. */
  behaviour?: TestBehaviour;
  mission: Mission | null;
  conversion: ConversionGoal | null;
  reportLanguage: ReportLanguage;
  /** Language of the site's content; null: unknown, the runner reads `<html lang>`. Absent from older servers. */
  siteLanguage?: SiteLanguage | null;
  /** Country the visitors browse from (ISO code). Absent from older servers. */
  market?: string | null;
  maxSteps: number;
  maxDurationMs: number;
  /** Development only: the runner may reach private addresses (localhost, a demo container). */
  allowPrivateNetwork: boolean;
  /**
   * Sign-in pages. `use`: sign in with `access`; `ask`: wait at a sign-in page for the person's answer;
   * `skip`: go on without an account. Absent from older servers: skip.
   */
  accessPolicy?: 'use' | 'ask' | 'skip';
  /** The project's access with its secret, when the policy is `use`. */
  access?: VisitAccessDto | null;
  /** Longest wait at sign-in pages over the visit, not counted in its time. */
  accessWaitMs?: number;
}

export interface SyntheticVisitSummaryDto {
  id: string;
  index: number;
  personaId: string | null;
  personaName: string;
  device: Device;
  variant: VisitVariant;
  status: VisitStatus;
  outcome: string | null;
  /** Why they left (abandoned, satisfied, out of time); null for conversions and older visits. */
  exit: VisitExit | null;
  /** What worked for this visitor, found by the analyst. */
  strengths: VisitStrength[];
  /** The visitor's simulated time on the site. */
  simulatedMs: number | null;
  stepCount: number;
  durationMs: number | null;
  finalMood: number | null;
  sessionId: string;
  startedAt: string | null;
  finishedAt: string | null;
  costUsd: number;
  /** Waiting at a sign-in page for the person's answer. Absent from older servers. */
  waitingForAccess?: boolean;
}

export interface SyntheticStepDto {
  index: number;
  ts: string;
  url: string;
  thought: string;
  mood: number;
  feeling: Feeling | null;
  action: AgentAction;
  result: string | null;
  /** Inspection: what was checked on this screen and found correct. */
  checked: string[];
  /** Simulated time of the visitor at the end of the step (ms since arrival). */
  clockMs: number | null;
}

export interface SyntheticIssueDto {
  id: string;
  visitId: string;
  stepIndex: number;
  ts: string;
  severity: IssueSeverity;
  title: string;
  description: string;
  url: string;
  personaName: string;
  sessionId: string;
}

/** What worked: at most three, each backed by what visitors did. */
export interface ReportStrength {
  title: string;
  description: string;
  /** Visits that show it, with the step to watch. */
  moments: { visitId: string; stepIndex: number }[];
}

/** Visitors who left for the same reason from the same page. */
export interface ReportExit {
  category: ExitReason;
  /** Path of the page they left from; null when unknown. */
  path: string | null;
  visitIds: string[];
  /** Their reasons, concrete (checked against the path). */
  reasons: string[];
}

/** Inspection: what was looked at, and what was checked and found correct. */
export interface ReportCoverage {
  pages: { path: string; visits: number }[];
  checkedOk: string[];
}

export interface SyntheticReportDto {
  language: ReportLanguage;
  /** The verdict in a few words (missing on reports written before the editing pass). */
  headline?: string;
  summary: string;
  highlights: string[];
  /** `fix`: the change to make, in one sentence (missing on older reports). */
  groups: { title: string; severity: IssueSeverity; description: string; issueIds: string[]; fix?: string }[];
  /** One prompt to paste into Lovable, Cursor or Bolt to fix the main issues (missing on older reports). */
  fixPrompt?: string;
  /** What worked (missing on older reports). */
  strengths?: ReportStrength[];
  /** Why people left, grouped by reason and page (missing on older reports and inspections). */
  exits?: ReportExit[];
  /** Inspection only. */
  coverage?: ReportCoverage;
  generatedAt: string;
}

export interface SyntheticTestSummaryDto {
  id: string;
  name: string;
  siteUrl: string;
  mode: VisitMode;
  behaviour: TestBehaviour;
  status: TestStatus;
  seed: number;
  visitsTotal: number;
  visitsQueued: number;
  visitsRunning: number;
  visitsFinished: number;
  /** Finished visits that count in the rates (converted, satisfied, abandoned). */
  visitsCounted: number;
  conversions: number;
  satisfied: number;
  abandoned: number;
  issues: number;
  /** The visits wait while their briefs are written from the site. */
  preparing: boolean;
  siteLanguage: SiteLanguage | null;
  market: string | null;
  costUsd: number;
  /** Estimated time to completion (null when finished or unknown). */
  etaMs: number | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface SyntheticTestDetailDto extends SyntheticTestSummaryDto {
  mission: Mission | null;
  conversion: ConversionGoal | null;
  reportLanguage: ReportLanguage;
  maxParallel: number;
  visits: SyntheticVisitSummaryDto[];
  issueList: SyntheticIssueDto[];
  report: SyntheticReportDto | null;
  reportError: string | null;
  /** A first test with gentle visitors: the report says so and offers a tougher test. */
  gentle: boolean;
  /** A test started from the home page: its public report and the instant review of the site. */
  home: { slug: string; siteReview: { status: SiteReviewStatus; review: SiteReviewDto | null } } | null;
  /** Sign-in pages: the saved access, the question to the person and the visitors waiting or stopped there. */
  access: TestAccessDto;
}

/** Recording chunks stored after a seq, to watch a visit while it runs (rrweb events, untyped here). */
export interface RecordingChunksDto {
  /** more chunks are stored after these: ask again right away */
  more: boolean;
  chunks: { seq: number; events: unknown[] }[];
}

export interface SyntheticVisitDetailDto extends SyntheticVisitSummaryDto {
  testId: string;
  testName: string;
  testBehaviour: TestBehaviour;
  persona: PersonaSnapshot;
  steps: SyntheticStepDto[];
  issues: SyntheticIssueDto[];
  error: string | null;
  inputTokens: number;
  outputTokens: number;
}
